import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';

type FormWideErrorsMap = Record<string, string>;

interface FormWideErrorContextValue {
  errors: FormWideErrorsMap;
  setErrors: (updater: (prev: FormWideErrorsMap) => FormWideErrorsMap) => void;
}

const noop = () => {};

const FormWideErrorContext = createContext<FormWideErrorContextValue>({
  errors: {},
  setErrors: noop,
});

export const FormWideErrorProvider = ({ children }: PropsWithChildren<{}>) => {
  const [errors, setErrors] = useState<FormWideErrorsMap>({});
  const stableSetErrors = useCallback(
    (updater: (prev: FormWideErrorsMap) => FormWideErrorsMap) => {
      setErrors((prev) => updater(prev));
    },
    [],
  );

  return (
    <FormWideErrorContext.Provider
      value={{
        errors,
        setErrors: stableSetErrors,
      }}
    >
      {children}
    </FormWideErrorContext.Provider>
  );
};

export function useFormWideErrors(): {
  errors: FormWideErrorsMap;
  setErrors: (updater: (prev: FormWideErrorsMap) => FormWideErrorsMap) => void;
};
export function useFormWideErrors(id: string): {
  error: string | undefined;
  setError: (value?: string) => void;
};
export function useFormWideErrors(id?: string):
  | {
      errors: FormWideErrorsMap;
      setErrors: (
        updater: (prev: FormWideErrorsMap) => FormWideErrorsMap,
      ) => void;
    }
  | { error: string | undefined; setError: (value?: string) => void } {
  const { errors, setErrors } = useContext(FormWideErrorContext);

  const setError = useCallback(
    (value?: string) => {
      if (!id) {
        // NB You should never be here - if there's no id we should be returning a different function
        return;
      }
      setErrors((prev) => {
        if (value === undefined) {
          const { [id]: _unused, ...rest } = prev;
          return rest;
        }
        return {
          ...prev,
          [id]: value,
        };
      });
    },
    [id, setErrors],
  );

  if (!id) {
    return {
      errors,
      setErrors,
    };
  }

  return {
    error: errors[id],
    setError,
  };
}
